import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useQRSession } from '../contexts/QRSessionContext';
import Navigation from '../components/navigation';
import QRDisplay from '../components/QRDisplay';
import config from '../config/environment';

const Classes = () => {
  const { token } = useAuth();
  const { 
    currentSession, 
    isActive, 
    createSession, 
    startQRGeneration, 
    loading, 
    error 
  } = useQRSession();

  const [modules, setModules] = useState([]);
  const [formData, setFormData] = useState({
    module_id: '',
    session_type: 'lecture',
    duration_minutes: 3,
    location: ''
  });
  const [step, setStep] = useState('form'); // 'form', 'created', 'active'

  // Fetch modules on component mount
  useEffect(() => {
    fetchModules();
  }, []);

  const fetchModules = async () => {
    try {
      const response = await fetch(config.getApiUrl('/qr-sessions/modules'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setModules(data.modules);
      }
    } catch (err) {
      console.error('Error fetching modules:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreateSession = async (e) => {
    e.preventDefault();
    
    if (!formData.module_id || !formData.session_type) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const session = await createSession(formData);
      setStep('created');
    } catch (err) {
      alert('Failed to create session: ' + err.message);
    }
  };

  const handleStartQR = async () => {
    try {
      await startQRGeneration(currentSession.session_id);
      setStep('active');
    } catch (err) {
      alert('Failed to start QR generation: ' + err.message);
    }
  };

  const handleNewSession = () => {
    setStep('form');
    setFormData({
      module_id: '',
      session_type: 'lecture',
      duration_minutes: 3,
      location: ''
    });
  };

  const selectedModule = modules.find(m => m.id === formData.module_id);

  return (
    <div>
      <Navigation />
      <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
        <h2 style={{ marginBottom: '30px', color: '#333' }}>
          📚 Class Sessions
        </h2>

        {error && (
          <div style={{
            backgroundColor: '#fee',
            color: '#c33',
            padding: '10px',
            borderRadius: '5px',
            marginBottom: '20px'
          }}>
            {error}
          </div>
        )}

        {step === 'form' && (
          <div style={{
            backgroundColor: '#f9f9f9',
            padding: '30px',
            borderRadius: '10px',
            border: '1px solid #ddd'
          }}>
            <h3 style={{ marginBottom: '20px' }}>Create New Session</h3>
            
            <form onSubmit={handleCreateSession}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Module *
                </label>
                <select
                  name="module_id"
                  value={formData.module_id}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ccc',
                    borderRadius: '5px',
                    fontSize: '16px'
                  }}
                >
                  <option value="">Select a module</option>
                  {modules.map(module => (
                    <option key={module.id} value={module.id}>
                      {module.module_code} - {module.module_name}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Session Type *
                </label>
                <select
                  name="session_type"
                  value={formData.session_type}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ccc',
                    borderRadius: '5px',
                    fontSize: '16px'
                  }}
                >
                  <option value="lecture">Lecture</option>
                  <option value="tutorial">Tutorial</option>
                  <option value="lab">Lab</option>
                  <option value="seminar">Seminar</option>
                </select>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  QR Duration (minutes)
                </label>
                <select
                  name="duration_minutes"
                  value={formData.duration_minutes}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ccc',
                    borderRadius: '5px',
                    fontSize: '16px'
                  }}
                >
                  <option value={1}>1 minute</option>
                  <option value={2}>2 minutes</option>
                  <option value={3}>3 minutes</option>
                  <option value={4}>4 minutes</option>
                  <option value={5}>5 minutes</option>
                </select>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Location (optional)
                </label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  placeholder="e.g., Room A101, Building 1"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ccc',
                    borderRadius: '5px',
                    fontSize: '16px'
                  }}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{
                  backgroundColor: '#007bff',
                  color: 'white',
                  padding: '12px 30px',
                  border: 'none',
                  borderRadius: '5px',
                  fontSize: '16px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1
                }}
              >
                {loading ? 'Creating...' : 'Create Session'}
              </button>
            </form>
          </div>
        )}

        {step === 'created' && currentSession && (
          <div style={{
            backgroundColor: '#f0f8ff',
            padding: '30px',
            borderRadius: '10px',
            border: '1px solid #007bff'
          }}>
            <h3 style={{ color: '#007bff', marginBottom: '20px' }}>
              ✅ Session Created Successfully
            </h3>
            
            <div style={{ marginBottom: '20px' }}>
              <p><strong>Session ID:</strong> {currentSession.session_id}</p>
              <p><strong>Module:</strong> {selectedModule?.module_code} - {selectedModule?.module_name}</p>
              <p><strong>Type:</strong> {currentSession.session_type}</p>
              <p><strong>Duration:</strong> {formData.duration_minutes} minutes</p>
              {currentSession.location && (
                <p><strong>Location:</strong> {currentSession.location}</p>
              )}
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={handleStartQR}
                disabled={loading}
                style={{
                  backgroundColor: '#28a745',
                  color: 'white',
                  padding: '12px 30px',
                  border: 'none',
                  borderRadius: '5px',
                  fontSize: '16px',
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? 'Starting...' : 'Generate QR Code'}
              </button>

              <button
                onClick={handleNewSession}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  padding: '12px 30px',
                  border: 'none',
                  borderRadius: '5px',
                  fontSize: '16px',
                  cursor: 'pointer'
                }}
              >
                New Session
              </button>
            </div>
          </div>
        )}

        {step === 'active' && isActive && (
          <QRDisplay 
            session={currentSession} 
            onNewSession={handleNewSession}
          />
        )}
      </div>
    </div>
  );
};

export default Classes;